<template>

  <div>

      <el-alert v-if="showWarning"
        type="info"
        title="No link to site found, you must select a site below."
        show-icon
        :closable="false">
      </el-alert>

    <component :is="el" :label="label" :required="required">
      <i v-if="noForm && label">{{label}}&nbsp;</i>

      <div v-if="allowScopeSelection" class="ss-search-row">
        <el-select
          v-model="scope"
          :disabled="disabled"
          aria-label="Search scope"
          class="ss-scope-select">
          <el-option
            v-for="opt in scopeOptions"
            :key="opt.value"
            :value="opt.value"
            :label="opt.label">
            <span>{{ opt.label }}</span>
            <span class="scope-desc">{{ opt.desc }}</span>
          </el-option>
        </el-select>

        <el-select
        v-model.trim="formData"
        value-key="id"
        :disabled="disabled"
        :placeholder="placeholder"
        :no-data-text="noDataText"
        filterable
        remote
        :remote-method="findSiteList"
        :loading="searching"
        loading-text="Searching…"
        class="ss-main-select"
        popper-class="ss-main-popper"
        @change="updated">

        <el-option
          v-for="site in filteredSites"
          :key="site.id"
          :label="site.siteName"
          :value="site">
          <span style="float: left">{{ site.siteName }}</span>
          <span style="float: right; color: #8492a6; font-size: 13px">
             <span v-if="site.telcoSiteId" class="text-muted">{{site.telcoSiteId}} |
             </span>{{ site.siteId }}</span>
          <span v-if="hintsBySite[site.id]" class="ss-hint">
            <span class="ss-hint-label">{{ hintsBySite[site.id].label }}:</span> {{ hintsBySite[site.id].value }}
          </span>
        </el-option>

        </el-select>
      </div>

      <el-select
        v-if="!allowScopeSelection"
        v-model.trim="formData"
        value-key="id"
        :disabled="disabled"
        :placeholder="placeholder"
        :no-data-text="noDataText"
        filterable
        remote
        :remote-method="findSiteList"
        :loading="searching"
        loading-text="Searching…"
        @change="updated">

        <el-option
          v-for="site in sites"
          :key="site.id"
          :label="site.siteName"
          :value="site">
          <span style="float: left">{{ site.siteName }}</span>
          <span style="float: right; color: #8492a6; font-size: 13px">
             <span v-if="site.telcoSiteId" class="text-muted">{{site.telcoSiteId}} |
             </span>{{ site.siteId }}</span>
        </el-option>

      </el-select>

    </component>

    <div v-if="showSite && formData" style="padding-top: 0.5em; padding-bottom: 0.75em;">
      <site-card ref="siteCard" :site-data="formData" :sharer-id="isTelco() ? this.org().id : null" map-right />
    </div>

  </div>

</template>

<script>

import SiteMixins from '@/mixins/SiteMixins'
import SiteCard from '@/components/sites/SiteCard'

const SCOPE_FIELDS = {
  all: null,
  nameId: ['siteName', 'siteId', 'telcoSiteId'],
  address: ['address', 'district', 'subdistrict', 'postCode', 'zone', 'state', 'region', 'country']
}

const SCOPE_OPTIONS = [
  { value: 'all', label: 'All fields', desc: 'name, ID, address…' },
  { value: 'nameId', label: 'Name + ID', desc: 'site name, site ID, telco ID' },
  { value: 'address', label: 'Address', desc: 'address, district, state…' }
]

const FIELD_LABELS = {
  siteId: 'Site ID',
  telcoSiteId: 'Telco Site ID',
  address: 'Address',
  district: 'District',
  subdistrict: 'Subdistrict',
  state: 'State',
  region: 'Region',
  country: 'Country',
  siteGroup: 'Site Group',
  postCode: 'Postcode',
  zone: 'Zone',
  status: 'Status'
}

const HINT_FIELD_ORDER = ['siteId', 'telcoSiteId', 'address', 'district', 'subdistrict', 'state', 'region', 'country', 'siteGroup', 'postCode', 'zone', 'status']

export default {
  name: 'select-site',

  mixins: [SiteMixins],

  components: {
    SiteCard
  },

  props: {

    label: {
      type: String,
      default: 'Select Site'
    },

    value: {
//      type: [String, Object, Number],
      required: true
    },

    sharer: {
      type: Number,
      default: null
    },

    provider: {
      type: Number,
      default: null
    },

    showSite: {
      type: Boolean,
      default: true
    },

    actionType: {
      type: String,
      default: null
    },

    excludeSharer: {
      type: Boolean,
      default: false
    },

    backhaulSearch: {
      type: Boolean,
      default: false
    },

    showWarning: {
      type: Boolean,
      default: false
    },

    disabled: {
      type: Boolean,
      default: false
    },

    required: {
      type: Boolean,
      default: false
    },

    fullObject: {
      type: Boolean,
      default: false
    },

    placeholder: {
      type: String,
      default: 'Enter site name or site Id (min 3 letters)'
    },

    helpText: {
      type: String,
      default: null
    },

    callback: {
      type: Function,
      default: null
    },

    noForm: {
      type: Boolean,
      default: true
    },

    defaultScope: {
      type: String,
      default: 'all',
      validator: function (v) { return ['all', 'nameId', 'address'].includes(v) }
    },

    allowScopeSelection: {
      type: Boolean,
      default: false
    },

    debounceMs: {
      type: Number,
      default: 250
    }

  },

  computed: {

    filteredSites () {
      if (!this.scope || this.scope === 'all' || !this.lastQuery) return this.sites
      var fields = SCOPE_FIELDS[this.scope]
      if (!fields) return this.sites
      var q = this.lastQuery.toLowerCase()
      return this.sites.filter(function (site) {
        return fields.some(function (f) { return site[f] && String(site[f]).toLowerCase().includes(q) })
      })
    },

    noDataText () {
      if (this.searching) return ''
      // XOL-393: don't say "no sites match" when we never ran a valid search.
      if (this.needsSharer) return 'Select an organisation first, then search for a site'
      if (!this.lastQuery) return 'Type at least 3 letters to search'
      if (this.lastQuery.length < 3) return 'Keep typing — at least 3 letters'
      if (this.searchError) return 'Search failed — check your connection and try again'
      if (this.filteredSites.length === 0 && this.scope !== 'all') {
        return 'No matches in this scope. Try "All fields" or a different keyword.'
      }
      if (this.filteredSites.length === 0) {
        var q = this.lastQuery.length > 40 ? this.lastQuery.slice(0, 40) + '…' : this.lastQuery
        return 'No sites match "' + q + '"'
      }
      return ''
    },

    scopeOptions () {
      return SCOPE_OPTIONS
    },

    hintsBySite () {
      var map = {}
      var sites = this.filteredSites
      for (var i = 0; i < sites.length; i++) {
        map[sites[i].id] = this.matchedHint(sites[i])
      }
      return map
    }

  },

  methods: {

    updated (site) {
      this.$emit('input', this.fullObject ? site : site.id)
      this.$emit('change', this.fullObject ? site : site.id)

      if (this.callback) {
        this.callback(site.id, site.siteName, site)
      }

      if (this.fullObject) {
        this.formData = site
      }

      this.$nextTick(() => {
        if (site) {
           this.$refs.siteCard.setSiteData(site)
        }
      })

    },

    findSiteList (value) {
      var trimmed = (value || '').trim()
      this.lastQuery = trimmed

      clearTimeout(this.debounceTimer)

      if (!trimmed || trimmed.length < 3) {
        this.requestSeq += 1
        this.searching = false
        this.searchError = false
        this.sites = []
        return
      }

      this.debounceTimer = setTimeout(() => {
        this.runSearch(trimmed)
      }, this.debounceMs)
    },

    runSearch (value) {
      this.requestSeq += 1
      var seq = this.requestSeq
      this.searching = true
      this.searchError = false

      // XOL-393: an actionType search is only meaningful sharer-scoped. Without a
      // sharer the URL degrades to /sites/{actionType}, which Laravel binds to a
      // catch-all filtering `sites.status LIKE '%RTx%'` — a lifecycle column, so it can
      // never match: 200 with zero rows, indistinguishable from "no eligible sites".
      // Scoped to RT* (the whole real domain of this prop) and exempting the backhaul
      // branch. See docs/plans/2026-07-27-xol-393-request-render-access-defects.md (5).
      if (this.actionType && /^RT/.test(this.actionType) && !this.sharer && !this.backhaulSearch) {
        // requestSeq was already bumped above, which invalidates any in-flight response.
        this.searching = false
        this.searchError = false
        this.needsSharer = true
        this.sites = []
        return
      }
      this.needsSharer = false

      var url
      if (this.backhaulSearch) {
        var sharerId = this.isTelco() ? this.org().id : this.sharer
        url = '/sites/backhaul/sharer' +
          (sharerId ? ('/' + sharerId) : '/null') +
          (this.provider ? '/' + this.provider : '') +
          '/?sort=siteName%7Casc&page=1&per_page=30&filter=' + value
      } else {
        url = '/sites' +
          (this.sharer ? ('/sharer/' + (this.excludeSharer ? 'not/' : '') + this.sharer) : '') +
          (this.actionType ? '/' + this.actionType : '') +
          '/?sort=siteName%7Casc&page=1&per_page=30&filter=' + value
      }

      this.fetchData(url, (response) => {
        this.handleResponse(seq, response)
      }, 'sites', () => {
        if (seq === this.requestSeq) {
          this.searching = false
          this.searchError = true
        }
      })
    },

    handleResponse (seq, response) {
      if (seq !== this.requestSeq) return
      this.sites = response
      this.searching = false
    },

    matchedHint (site) {
      if (!this.lastQuery) return null
      var q = this.lastQuery.toLowerCase()
      if (site.siteName && site.siteName.toLowerCase().includes(q)) return null
      for (var i = 0; i < HINT_FIELD_ORDER.length; i++) {
        var f = HINT_FIELD_ORDER[i]
        var v = site[f]
        if (v && String(v).toLowerCase().includes(q)) {
          return { label: FIELD_LABELS[f], value: String(v) }
        }
      }
      return null
    }

  },

  beforeDestroy () {
    clearTimeout(this.debounceTimer)
  },

  data () {

    return {
      formData: this.value,
      searching: false,
      searchError: false,
      sites: [],
      el: this.noForm ? 'div' : 'el-form-item',
      scope: this.defaultScope,
      lastQuery: '',
      debounceTimer: null,
      requestSeq: 0,
      needsSharer: false
    }
  }, // data

}
</script>

<style scoped>
  .el-select {
    display: inline-block;
    width: 100%;
  }
  .ss-search-row {
    display: flex;
    flex-wrap: wrap;
    gap: 0;
    align-items: stretch;
  }
  .ss-scope-select {
    width: 150px;
    flex: 0 0 150px;
  }
  .ss-scope-select ::v-deep .el-input__inner {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
  .ss-main-select {
    flex: 1;
  }
  .ss-main-select ::v-deep .el-input__inner {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }
  .scope-desc {
    font-size: 11px;
    color: #8492a6;
    margin-left: 6px;
  }
  .ss-hint {
    display: block;
    clear: both;
    font-size: 12px;
    color: #8492a6;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .ss-hint-label {
    color: #606266;
    font-weight: 600;
  }
</style>

<style>
  /* Non-scoped because the popper is portaled to document.body and does not
     carry SelectSite's data-v attribute. Targeted via popper-class so this
     does not affect any other el-select dropdown in the app. */
  .ss-main-popper .el-select-dropdown__item {
    height: auto;
    min-height: 34px;
    line-height: 1.4;
    padding-top: 6px;
    padding-bottom: 6px;
  }
  .ss-main-popper .el-select-dropdown__item > span[style*="float: left"] {
    line-height: 22px;
  }
  .ss-main-popper .el-select-dropdown__item > span[style*="float: right"] {
    line-height: 22px;
  }
</style>
